import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@saliente/common-auth';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

/**
 * Must be eagerly instantiated (inject into {@code AppComponent}) so the subscriptions
 * fire on app boot — otherwise the first {@code isClientEnabled} check returns false
 * until some other consumer triggers DI.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
	private readonly _clientEnabled$ = new BehaviorSubject<Set<string>>(new Set());
	readonly clientEnabled$: Observable<Set<string>> = this._clientEnabled$.asObservable();

	private readonly _userEnabled$ = new BehaviorSubject<Set<string>>(new Set());
	readonly userEnabled$: Observable<Set<string>> = this._userEnabled$.asObservable();

	private readonly _otherClients = new Map<string, Observable<Set<string>>>();

	constructor(private http: HttpClient, private authService: AuthService) {
		// No takeUntil / unsubscribe — service is providedIn: 'root', so it lives for
		// the app's lifetime and the subscriptions die with the page.
		this.authService.getSelectedCompanyId().subscribe((clientEid) => {
			if (!clientEid) {
				this._clientEnabled$.next(new Set());
				return;
			}
			this.loadForClient(clientEid);
		});

		this.authService.isDoneLoading$.subscribe(() => {
			const userEid = this.authService.user?.externalId;
			if (!userEid) {
				this._userEnabled$.next(new Set());
				return;
			}
			this.loadForUser(userEid);
		});
	}

	isClientEnabled(cod: string): boolean {
		return this._clientEnabled$.value.has(cod);
	}

	/** Reactive flavor — prefer this in templates so the view updates when the flag set loads. */
	isClientEnabled$(cod: string): Observable<boolean> {
		return this.clientEnabled$.pipe(map((set) => set.has(cod)));
	}

	/**
	 * For a client other than the selected company - an expert's Connect room can belong to any of
	 * their clients. Read once per client and kept for the page's lifetime, like the sets above.
	 */
	isEnabledForClient$(clientEid: string, cod: string): Observable<boolean> {
		if (!this._otherClients.has(clientEid)) {
			this._otherClients.set(clientEid, this.http.get<string[]>(`/features/clients/${clientEid}`, { headers: this.authHeaders() }).pipe(
				map((cods) => new Set(cods || [])),
				catchError(() => of(new Set<string>())), // fail closed
				shareReplay(1)
			));
		}
		return this._otherClients.get(clientEid).pipe(map((set) => set.has(cod)));
	}

	isUserEnabled(cod: string): boolean {
		return this._userEnabled$.value.has(cod);
	}

	isUserEnabled$(cod: string): Observable<boolean> {
		return this.userEnabled$.pipe(map((set) => set.has(cod)));
	}

	private loadForClient(clientEid: string): void {
		this.http.get<string[]>(`/features/clients/${clientEid}`, { headers: this.authHeaders() }).subscribe({
			next: (cods) => this._clientEnabled$.next(new Set(cods || [])),
			error: () => this._clientEnabled$.next(new Set()), // fail closed
		});
	}

	private loadForUser(userEid: string): void {
		this.http.get<string[]>(`/features/users/${encodeURIComponent(userEid)}`, { headers: this.authHeaders() }).subscribe({
			next: (cods) => this._userEnabled$.next(new Set(cods || [])),
			error: () => this._userEnabled$.next(new Set()), // fail closed
		});
	}

	private authHeaders(): HttpHeaders {
		return new HttpHeaders().set('authorization', this.authService.authHeaderValue());
	}
}
